import { Check, Circle, MapPin } from "lucide-react"
import { cn } from "@/lib/utils"
import type {
  OrderCard,
  OrderCardStop,
  OrderCardStopState,
} from "@/lib/aiWidgetOrderParser"
import { formatMin } from "../tachoCard/tachoCardFormat"
import { formatEta, formatWindow } from "./orderCardFormat"
import { ORDER_CARD_STRINGS, type OrderCardStrings } from "./orderCardStrings"

const STATE_ICON: Record<
  OrderCardStopState,
  React.ComponentType<{ className?: string }>
> = { done: Check, here: MapPin, ahead: Circle }

function StopRow({ stop, t }: { stop: OrderCardStop; t: OrderCardStrings }) {
  const Icon = STATE_ICON[stop.state]
  const place = [stop.locality, stop.countryCode].filter(Boolean).join(", ")
  const title = [stop.label, place].filter(Boolean).join(" · ")
  const eta = formatEta(stop, t)
  return (
    <li
      data-testid="order-card-stop"
      data-state={stop.state}
      className="grid grid-cols-[1.25rem_1fr_auto] items-start gap-2 py-1.5"
    >
      <Icon
        aria-label={t.states[stop.state]}
        className={cn(
          "mt-0.5 h-4 w-4",
          stop.state === "done" ? "text-muted-foreground" : "text-foreground",
        )}
      />
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {stop.address && (
          <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {stop.address}
          </div>
        )}
      </div>
      <div className="text-right text-xs text-muted-foreground">
        {stop.window && <div>{formatWindow(stop.window, t)}</div>}
        {eta && (
          <div>
            {eta}
            {stop.etaRestMin !== null &&
              ` · ${t.withRest(formatMin(stop.etaRestMin))}`}
          </div>
        )}
        {stop.state !== "done" && stop.lateMin !== null && stop.lateMin > 0 && (
          <span
            data-testid="order-card-late"
            className="mt-1 inline-block rounded-md border border-border bg-muted px-1.5 py-0.5 font-medium text-foreground"
          >
            {t.late(formatMin(stop.lateMin))}
          </span>
        )}
      </div>
    </li>
  )
}

export function AIChatOrderCard({ card }: { card: OrderCard }) {
  const t = ORDER_CARD_STRINGS[card.language]
  const who = [card.plate, card.driverName !== card.plate && card.driverName]
    .filter(Boolean)
    .join(" · ")
  return (
    <div
      data-testid="order-card"
      className="mt-3 rounded-xl border border-border bg-card p-4"
    >
      {card.orderNumber === null ? (
        who && <div className="text-base font-semibold">{who}</div>
      ) : (
        <>
          <div className="text-base font-semibold">
            {t.title(card.orderNumber)}
          </div>
          {who && <div className="text-sm text-muted-foreground">{who}</div>}
        </>
      )}
      <ul className="mt-2">
        {card.stops.map((stop, i) => (
          <StopRow key={i} stop={stop} t={t} />
        ))}
      </ul>
      {card.next && (
        <div className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
          {t.next(
            `${Math.round(card.next.km)} km`,
            formatMin(card.next.drivingMin),
          )}
        </div>
      )}
    </div>
  )
}
